import {combineLatest as observableCombineLatest, of as observableOf, Observable} from 'rxjs';

import {takeWhile, catchError, map} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone} from '@angular/core';
import {AbstractControlComponent} from './abstract-control';
import {ValidatorFn, Validators} from '@angular/forms';
import {ViewTabComponent} from '../viewtab.component';
import {ErrorService} from '../../../error/error.service';

@Component({
  selector: 'ctrl-slider',
  template: `
    @if ((getCaughtObservable(isEnabled) | async) != true) {
      <p>{{ readonlyValue }} {{ getCaughtObservable(postfix) | async }}</p>
    }
    @if (getCaughtObservable(isEnabled) | async) {
      <div>
        <!-- TODO: The 'invert' property no longer exists -->
        <!-- TODO: The 'tickInterval' property no longer exists -->
        <!-- TODO: The 'vertical' property no longer exists -->
        <mat-slider
          [max]="getCaughtObservable(max) | async"
          [min]="getCaughtObservable(min) | async"
          [step]="getCaughtObservable(step) | async"
          [thumbLabel]="thumbLabel"
          [(ngModel)]="value"
          ><input matSliderThumb
        /></mat-slider>
        <mat-form-field floatLabel="never">
          <input
            matInput
            [(ngModel)]="value"
            type="number"
            [min]="getCaughtObservable(min) | async"
            [max]="getCaughtObservable(max) | async"
            [step]="getCaughtObservable(step) | async"
          />
        </mat-form-field>
        <span>{{ getCaughtObservable(postfix) | async }}</span>
        @if (formControl.touched) {
          <div>
            @if (formControl.hasError('pattern') || formControl.hasError('required')) {
              <mat-error>
                <language
                  key="ControlErrorRequired"
                  defaultValue="This field is required."
                ></language>
              </mat-error>
            }
            @if (formControl.hasError('min')) {
              <mat-error>
                <language
                  key="ControlErrorMin"
                  defaultValue="Please choose a value greater or equal to {0}."
                  [args]="[getCaughtObservable(min) | async]"
                ></language>
              </mat-error>
            }
            @if (formControl.hasError('max')) {
              <mat-error>
                <language
                  key="ControlErrorMax"
                  defaultValue="Please choose a value lower or equal to {0}."
                  [args]="[getCaughtObservable(max) | async]"
                ></language>
              </mat-error>
            }
          </div>
        }
      </div>
    }
  `,
  styleUrls: ['./ctrl-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlSliderComponent extends AbstractControlComponent {
  constructor(
    _parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
  ) {
    super(_parent, null, null, changeDetector, _ngZone, errorService);
  }

  autoTicks = false;
  invert = false;
  showTicks = false;
  thumbLabel = true;
  vertical = false;

  min: Observable<number> = observableOf(0);
  max: Observable<number> = observableOf(255);
  step: Observable<number> = observableOf(1);

  private _value: number = null;

  protected initValidators(): void {
    this.min = this._config
      .getPropertyValues('MinValue')
      .pipe(map(val => CtrlSliderComponent.parseMyInt(val, 0)));
    this.max = this._config
      .getPropertyValues('MaxValue')
      .pipe(map(val => CtrlSliderComponent.parseMyInt(val, 255)));
    this.step = this._config
      .getPropertyValues('StepSize')
      .pipe(map(val => CtrlSliderComponent.parseMyInt(val, 1)));

    observableCombineLatest(
      this._config.getBooleanPropertyValues('Required'),
      this.min,
      this.max,
      (required: boolean, min: any, max: any) => {
        const validators: ValidatorFn[] = [];
        if (required === true) {
          validators.push(Validators.required);
          validators.push(Validators.pattern(/^\d+$/));
        }

        if (min) {
          validators.push(Validators.min(min));
        }

        if (max) {
          validators.push(Validators.max(max));
        }

        this.formControl.setValidators(validators);
        this.formControl.updateValueAndValidity({emitEvent: false});
      },
    )
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
        takeWhile(() => this.alive),
      )
      .subscribe();
  }

  protected formatSource(value: any): any {
    if (typeof value !== 'number') {
      value = CtrlSliderComponent.parseMyInt(value, null);
    }
    this.updateValueFromSource(value);
    return value;
  }

  protected formatTarget(value: any): any {
    return value === '?' ? null : value;
  }

  get tickInterval(): number | 'auto' {
    return this.showTicks ? (this.autoTicks ? 'auto' : this._tickInterval) : null;
  }

  private _tickInterval = 1;

  get postfix(): Observable<string> {
    return this._config.getPropertyValues('Postfix').pipe(
      map(val => {
        if (val == null) {
          return '';
        }
        return val;
      }),
    );
  }

  private static parseMyInt(input: string, defaultValue: number): number {
    if (input == null) return defaultValue;
    let num = Number(input);
    if (num == null) {
      num = defaultValue;
    }
    return num;
  }

  private updateValueFromSource(value: number): void {
    this._value = value;
    this.changeDetector.detectChanges();
  }

  set value(value: number) {
    this._value = value;
    this.formControl.setValue(this._value, {emitEvent: true});
    this.formControl.markAsTouched();
    this.changeDetector.detectChanges();
  }

  get value(): number {
    return this._value;
  }

  get readonlyValue(): string {
    return this._value ? String(this._value) : '?';
  }
}
